import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { CvlApplicationStatusEnumType } from '__generated__/graphql';
import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import AccreditationApplicationStep from './AccreditationApplicationStep';
import AccreditationOnlineStepper from './AccreditationOnlineStepper';
import { AccreditationTab, Step } from './StepTypes';
import SubmitApplicationStep from './SubmitApplicationStep';
import UserRegistrationStep from './UserRegistrationStep';

const INVESTMENT_ENTITIES_QUERY = gql(`
  query FetchInvestmentEntitiesForAccreditationOnline {
    dashboard {
      id
      investmentEntities {
        id
        name
        status
        type
        kyc {
          id
          pan
          dateOfBirth
          address {
            lineOne
            lineTwo
            city
            state
            country
            pincode
          }
        }
        user {
          id
          email
          phoneNumber
        }
        createdAt
        updatedAt
      }
      lastUsedInvestmentEntity {
        id
      }
    }
  }
`);

const CVL_INVESTOR_APPLICATION_STATUS_QUERY = gql(`
  query InvestorAccreditationApplicationStatusForAccreditationOnline($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      status
    }
  }
`);

const AccreditationOnlinePage: FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [currentTab, setCurrentTab] = useState<AccreditationTab>('personal');
  const [investmentEntityId, setInvestmentEntityId] = useState('');

  const [searchParams] = useSearchParams();
  const statusFromQuery = searchParams.get('status');
  const investmentEntityIdFromQuery = searchParams.get('investmentEntityId');

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITIES_QUERY);

  const { data: cvlStatusData } = useQuery(CVL_INVESTOR_APPLICATION_STATUS_QUERY, {
    variables: { investmentEntityId },
    skip: !investmentEntityId,
    fetchPolicy: 'network-only',
  });

  const applicationStatus = cvlStatusData?.investorAccreditationApplication?.status;
  const isApplicationLocked =
    applicationStatus === CvlApplicationStatusEnumType.ApplicationSubmitted ||
    applicationStatus === CvlApplicationStatusEnumType.ApplicationVerified;

  useEffect(() => {
    if (!data || investmentEntityId) return;

    // Prefer explicit investmentEntityId from URL (e.g. after payment redirect)
    const fromQuery = investmentEntityIdFromQuery;
    if (fromQuery && data.dashboard.investmentEntities.some(entity => entity.id === fromQuery)) {
      setInvestmentEntityId(fromQuery);
      return;
    }

    // Fallback to last used investment entity if none provided in URL
    const lastUsedId = data.dashboard.lastUsedInvestmentEntity?.id;
    if (lastUsedId) {
      setInvestmentEntityId(lastUsedId);
    }
  }, [data, investmentEntityId, investmentEntityIdFromQuery]);

  useEffect(() => {
    if (!statusFromQuery || statusFromQuery !== 'success') return;
    setCurrentStep(2);
    setCurrentTab('payment');
  }, [statusFromQuery]);

  useEffect(() => {
    if (!isApplicationLocked) return;
    // Once the backend locks the application, do not allow Step 2 editing.
    // If the user is currently in Step 2, move them to Step 3.
    // If they are in Step 1, keep them there (they can explicitly proceed to Step 3).
    if (currentStep === 2) setCurrentStep(3);
  }, [isApplicationLocked, currentStep]);

  if (loading)
    return (
      <SidebarLayout>
        <SidebarLayoutContent>Loading...</SidebarLayoutContent>
      </SidebarLayout>
    );

  if (error || !data)
    return (
      <SidebarLayout>
        <SidebarLayoutContent>
          <ErrorMessage error={error} refetch={refetch} />
        </SidebarLayoutContent>
      </SidebarLayout>
    );

  const investmentEntities = data.dashboard.investmentEntities;
  const selectedInvestmentEntity =
    investmentEntities.find(entity => entity.id === investmentEntityId) ?? null;

  const handleNextStepFromRegistration = (options?: {
    skipPersonalDetails?: boolean;
    skipFinancialDetails?: boolean;
    skipDocuments?: boolean;
    goToSubmitStep?: boolean;
  }) => {
    if (options?.goToSubmitStep) {
      setCurrentStep(3);
      return;
    }
    setCurrentStep(2);
    if (options?.skipPersonalDetails && options?.skipFinancialDetails && options?.skipDocuments) {
      setCurrentTab('payment');
    } else if (options?.skipPersonalDetails && options?.skipFinancialDetails) {
      setCurrentTab('documents');
    } else if (options?.skipPersonalDetails) {
      setCurrentTab('financial');
    } else {
      setCurrentTab('personal');
    }
  };

  const renderContent = () => {
    return (
      <>
        <SidebarLayoutNavigation
          title="Accreditation"
          subTitle="Complete your accreditation process in three simple steps"
        />
        <SidebarLayoutContent>
          <div className="mb-24">
            <AccreditationOnlineStepper
              currentStep={currentStep}
              onStepChange={step => {
                // When locked, allow navigating to Step 1 or Step 3, but never Step 2.
                if (isApplicationLocked && step === 2) return;
                setCurrentStep(step);
              }}
              disabledSteps={isApplicationLocked ? [2] : undefined}
            />
            {currentStep === 1 && (
              <UserRegistrationStep
                investmentEntities={investmentEntities}
                investmentEntityId={investmentEntityId}
                onInvestmentEntityChange={setInvestmentEntityId}
                selectedInvestmentEntity={selectedInvestmentEntity}
                onNextStep={handleNextStepFromRegistration}
                locked={isApplicationLocked}
              />
            )}
            {currentStep === 2 && (
              <AccreditationApplicationStep
                currentTab={currentTab}
                onTabChange={setCurrentTab}
                selectedInvestmentEntity={selectedInvestmentEntity}
                onBackStep={() => {
                  if (isApplicationLocked) return;
                  setCurrentStep(1);
                }}
                onProceedToSubmitStep={() => setCurrentStep(3)}
                locked={isApplicationLocked}
              />
            )}
            {currentStep === 3 && (
              <SubmitApplicationStep selectedInvestmentEntity={selectedInvestmentEntity} />
            )}
          </div>
        </SidebarLayoutContent>
      </>
    );
  };

  return <SidebarLayout>{renderContent()}</SidebarLayout>;
};

export default AccreditationOnlinePage;
